import { useState } from "react";
import { useTranslations } from "next-intl";
import { apiErrorText } from "@/lib/api-error";
import { joinCourseWaitlist } from "@/services/api/courses";

type Props = {
  courseId: string;
  userEmail: string;
  userName?: string | null;
};

export function CourseWaitlistPanel({ courseId, userEmail, userName }: Props) {
  const t = useTranslations("waitlist");
  const tRoot = useTranslations();
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleJoin() {
    setLoading(true);
    setError(null);
    try {
      await joinCourseWaitlist(courseId, { email: userEmail, name: userName ?? undefined });
      setJoined(true);
    } catch (err) {
      // SPA-5 (C4): the API's code, translated — never its English message.
      setError(apiErrorText(err, tRoot, "waitlist.joinFailed", { namespace: "waitlist.errors" }));
    } finally {
      setLoading(false);
    }
  }

  if (joined) {
    return (
      <p className="mt-6 text-sm text-green-700 dark:text-green-400" data-testid="waitlist-joined">
        {t("joined")}
      </p>
    );
  }

  return (
    <div className="mt-6" data-testid="course-waitlist-panel">
      <p className="text-sm text-[var(--text-muted)]">{t("hint")}</p>
      <button
        type="button"
        onClick={() => void handleJoin()}
        disabled={loading}
        className="mt-3 rounded-md bg-[var(--accent-strong)] px-4 py-2 text-sm text-white hover:bg-[var(--accent-strong-hover)] disabled:opacity-60"
        data-testid="course-waitlist-join"
      >
        {loading ? t("working") : t("join")}
      </button>
      {error ? (
        <p className="mt-2 text-sm text-red-600" data-testid="course-waitlist-error">
          {error}
        </p>
      ) : null}
    </div>
  );
}
